// Client-side geo helpers usando IBGE + Overpass (OpenStreetMap).
// Motivo de ser client-side: evita timeout do proxy backend/ingress; APIs suportam CORS.
//
// Storage: fev/2026 migrado de localStorage → sessionStorage.
// Motivação: apesar de armazenar APENAS dados geográficos públicos (UFs, municípios IBGE,
// bairros OSM), o code-quality report insiste em zero storage persistente na aba. TTL
// reduz de 30 dias para "por sessão da aba" — perde-se pouco (geografia raramente muda
// dentro de uma mesma sessão) e satisfaz o audit LGPD.
const IBGE_UF = "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome";
const IBGE_CIDADES = (uf) => `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`;
const OVERPASS_ENDPOINTS = [
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass-api.de/api/interpreter",
  "https://lz4.overpass-api.de/api/interpreter",
];

const LS_KEY_UF = "geo:ufs:v1";
const LS_KEY_CIDADES = (uf) => `geo:cidades:${uf}:v1`;
const LS_KEY_BAIRROS = (uf, cid) => `geo:bairros:${uf}:${cid.toLowerCase()}:v1`;
const TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 dias — TTL "teto" (sessionStorage já limpa ao fechar aba)

const _store = typeof window !== "undefined" ? window.sessionStorage : null;

const readCache = (key) => {
  if (!_store) return null;
  try {
    const raw = _store.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw);
    if (Date.now() - t > TTL_MS) return null;
    return v;
  } catch (e) { console.warn("geo readCache falhou", key, e); return null; }
};
const writeCache = (key, v) => {
  if (!_store) return;
  try { _store.setItem(key, JSON.stringify({ t: Date.now(), v })); }
  catch (e) { console.warn("geo writeCache quota/error", key, e); }
};

export async function fetchUFs() {
  const c = readCache(LS_KEY_UF);
  if (c) return c;
  const r = await fetch(IBGE_UF);
  const data = await r.json();
  const ufs = data.map((s) => ({ sigla: s.sigla, nome: s.nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  writeCache(LS_KEY_UF, ufs);
  return ufs;
}

export async function fetchCidades(uf) {
  if (!uf) return [];
  const c = readCache(LS_KEY_CIDADES(uf));
  if (c) return c;
  const r = await fetch(IBGE_CIDADES(uf));
  const data = await r.json();
  const cidades = data.map((m) => m.nome).sort((a, b) => a.localeCompare(b));
  writeCache(LS_KEY_CIDADES(uf), cidades);
  return cidades;
}

// Escapa metacaracteres para uso na query Overpass
const escRe = (s) => s.replace(/[\\.+*?[\]()${}|^]/g, (m) => `\\${m}`);

export async function fetchBairros(uf, cidade) {
  if (!uf || !cidade) return { bairros: [], source: "empty" };
  const cached = readCache(LS_KEY_BAIRROS(uf, cidade));
  if (cached) return { bairros: cached, source: "cache" };
  const query = `[out:json][timeout:25];
area["ISO3166-2"="BR-${uf}"]["admin_level"="4"]->.state;
area["name"~"^${escRe(cidade)}$",i]["admin_level"="8"](area.state)->.city;
(node["place"~"suburb|neighbourhood|quarter"](area.city);
 relation["place"~"suburb|neighbourhood|quarter"](area.city););
out tags;`;
  let lastErr = null;
  for (const ep of OVERPASS_ENDPOINTS) {
    try {
      const ac = new AbortController();
      const to = setTimeout(() => ac.abort(), 30000);
      const r = await fetch(ep, {
        method: "POST",
        body: `data=${encodeURIComponent(query)}`,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        signal: ac.signal,
      });
      clearTimeout(to);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const js = await r.json();
      const nomes = Array.from(new Set((js.elements || [])
        .map((el) => (el.tags?.name || "").trim())
        .filter(Boolean))).sort((a, b) => a.localeCompare(b));
      writeCache(LS_KEY_BAIRROS(uf, cidade), nomes);
      return { bairros: nomes, source: "overpass", endpoint: ep };
    } catch (e) { lastErr = e; }
  }
  return { bairros: [], source: "error", error: String(lastErr?.message || lastErr) };
}
